import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Tag } from 'lucide-react';

import { cn } from '@/lib/utils';

import { formatCompactMoney } from '../customer-derived';
import type { QuickInsight } from './quick-insights';
import { useRationale } from './useRationale';

interface ProductInsightsRowProps {
  insights: QuickInsight[];
  appliedId: string | null;
  /** Toggle: apply the insight, or (if it's already applied) clear it. */
  onSelect: (qi: QuickInsight) => void;
  onHide: () => void;
}

/**
 * The Product Insights row (SCRUM-208 → v7 alignment SCRUM-360). "High-impact
 * campaigns, ready to launch" — a transparent strip of insight cards under the
 * utility strip. Each card shows the product, AI rationale (SCRUM-51), a
 * collapsible eligibility list, and snapshot tags.
 *
 * v7 behavior (app-shell 7): the whole card body toggles the insight. Applying
 * one insight expands that card (`selected`, "Remove insight") and MINIMIZES all
 * others (title-only). Re-clicking the applied card clears the filter. There is
 * no per-card "Start" button. A prev/next pager + edge-fade mask handle overflow.
 */
export function ProductInsightsRow({
  insights,
  appliedId,
  onSelect,
  onHide,
}: ProductInsightsRowProps) {
  const [openQualifiers, setOpenQualifiers] = useState<Set<string>>(new Set());
  const rowRef = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState<'none' | 'at-start' | 'at-middle' | 'at-end'>('at-start');

  const hasSelection = appliedId !== null;

  function toggleQualifiers(id: string) {
    setOpenQualifiers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // v7 edge-fade mask (O2/SCRUM-365): fade the leading/trailing edge based on
  // scroll position so off-screen cards read as a ghost. Recomputed on scroll +
  // whenever the card set changes (minimize changes total width).
  function updateEdge() {
    const row = rowRef.current;
    if (!row) return;
    const hasOverflow = row.scrollWidth > row.clientWidth + 2;
    const atStart = row.scrollLeft <= 2;
    const atEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 2;
    if (!hasOverflow) setEdge('none');
    else if (atStart) setEdge('at-start');
    else if (atEnd) setEdge('at-end');
    else setEdge('at-middle');
  }

  useEffect(() => {
    updateEdge();
    const row = rowRef.current;
    if (!row) return;
    const ro = new ResizeObserver(updateEdge);
    ro.observe(row);
    return () => {
      ro.disconnect();
    };
  }, [insights.length, appliedId]);

  function scrollBy(delta: number) {
    rowRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  }

  const canPrev = edge === 'at-middle' || edge === 'at-end';
  const canNext = edge === 'at-start' || edge === 'at-middle';
  const showPager = edge !== 'none';

  // v7 mask-image per edge state (SCRUM-365). Kept inline — it's a single
  // computed background-mask string, not reusable across the app.
  const maskStyle: React.CSSProperties =
    edge === 'at-start'
      ? { maskImage: 'linear-gradient(to right, black calc(100% - 80px), transparent)' }
      : edge === 'at-middle'
        ? {
            maskImage:
              'linear-gradient(to right, transparent, black 60px, black calc(100% - 80px), transparent)',
          }
        : edge === 'at-end'
          ? { maskImage: 'linear-gradient(to right, transparent, black 60px)' }
          : {};

  return (
    <div className="mb-4 flex flex-col gap-2">
      {/* v7 .quick-insights__header — small mono-uppercase label, no panel/Sparkles (D4). */}
      <div className="flex items-center justify-between px-1">
        <span className="text-text-3 font-mono text-[0.625rem] font-semibold tracking-[0.08em] uppercase">
          High-impact campaigns, ready to launch
        </span>
        <div className="flex items-center gap-3">
          {showPager && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => {
                  scrollBy(-296);
                }}
                disabled={!canPrev}
                aria-label="Scroll insights left"
                className="text-text-3 enabled:hover:text-brand-primary enabled:hover:bg-brand-dim rounded-tk-sm inline-flex h-[18px] w-[18px] items-center justify-center transition-colors disabled:cursor-not-allowed disabled:opacity-25"
              >
                <ChevronLeft className="h-2.5 w-2.5" />
              </button>
              <button
                type="button"
                onClick={() => {
                  scrollBy(296);
                }}
                disabled={!canNext}
                aria-label="Scroll insights right"
                className="text-text-3 enabled:hover:text-brand-primary enabled:hover:bg-brand-dim rounded-tk-sm inline-flex h-[18px] w-[18px] items-center justify-center transition-colors disabled:cursor-not-allowed disabled:opacity-25"
              >
                <ChevronRight className="h-2.5 w-2.5" />
              </button>
            </div>
          )}
          <button type="button" onClick={onHide} className="text-text-3 hover:text-text-1 text-xs">
            Hide
          </button>
        </div>
      </div>

      {insights.length === 0 ? (
        <p className="text-text-3 px-1 py-2 text-center text-sm">
          No actionable opportunities right now — re-check after the next intelligence batch.
        </p>
      ) : (
        <div
          ref={rowRef}
          onScroll={updateEdge}
          style={maskStyle}
          className="flex [scrollbar-width:none] gap-4 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden"
        >
          {insights.map((qi) => (
            <InsightCard
              key={qi.id}
              qi={qi}
              isApplied={appliedId === qi.id}
              isMinimized={hasSelection && appliedId !== qi.id}
              qualOpen={openQualifiers.has(qi.id)}
              onToggleQualifiers={() => {
                toggleQualifiers(qi.id);
              }}
              onSelect={() => {
                onSelect(qi);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── InsightCard sub-component ─────────────────────────────────────────────────
// Extracted so useRationale can be called at the top level of a component
// (React Rules of Hooks: hooks cannot be called inside a .map() callback).

interface InsightCardProps {
  qi: QuickInsight;
  isApplied: boolean;
  isMinimized: boolean;
  qualOpen: boolean;
  onToggleQualifiers: () => void;
  onSelect: () => void;
}

function InsightCard({
  qi,
  isApplied,
  isMinimized,
  qualOpen,
  onToggleQualifiers,
  onSelect,
}: InsightCardProps) {
  // Fetch AI-generated rationale for this product+insight pairing (SCRUM-51).
  // Context is aggregate (non-PII): insight label + matched customer count.
  const { data: rationaleData, isLoading: rationaleLoading } = useRationale({
    productName: qi.product.name,
    icpMatchCount: qi.product.requiredMatches,
    facts: [
      { label: 'Insight type', value: qi.insightLabel },
      { label: 'Matched customers', value: qi.count.toString() },
    ],
  });

  const rationaleText =
    rationaleData?.content !== '' && rationaleData?.content ? rationaleData.content : qi.rationale;
  const isAiGenerated = rationaleData?.aiGenerated ?? false;

  // v7 card body is a role=button toggle (SCRUM-58 a11y pattern: an interactive
  // wrapper, not onClick on a bare div). Eligibility toggle + the Apply/Remove
  // button stopPropagation so they don't also fire the card-level select.
  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  }

  // Minimized (v7 .qi-card.minimized): title-only, dimmed, FIXED width so the
  // strip does not reflow when a sibling is applied (R2). Conditional render of
  // the body rather than CSS display:none (React idiom, lighter DOM).
  if (isMinimized) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={handleKeyDown}
        aria-label={`Apply ${qi.product.name} insight`}
        className="bg-bg-1 border-border-0 hover:bg-bg-2 flex w-[268px] flex-shrink-0 cursor-pointer items-center gap-2 rounded-md border px-3 py-2.5 transition-colors"
      >
        <span className="bg-bg-3 text-text-3 inline-flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-sm">
          <Tag className="h-3.5 w-3.5" />
        </span>
        <span className="text-text-2 truncate text-[0.8125rem] font-semibold">
          {qi.product.name}
        </span>
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      aria-pressed={isApplied}
      className={cn(
        'flex w-[268px] flex-shrink-0 cursor-pointer flex-col rounded-md border p-3 transition-all',
        isApplied
          ? 'bg-bg-4 border-border-2 shadow-tk-sm'
          : 'bg-bg-3 border-border-0 hover:bg-bg-4 hover:border-border-1',
      )}
    >
      {/* Header: v7 product-icon (cyan-dim square) + title */}
      <div className="mb-1.5 flex items-center gap-2">
        <span className="bg-brand-dim text-brand-primary inline-flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-sm">
          <Tag className="h-3.5 w-3.5" />
        </span>
        <div className="font-display text-text-0 min-w-0 flex-1 text-[0.9375rem] leading-tight font-semibold">
          {qi.product.name}
        </div>
      </div>

      {/* Rationale — loading skeleton, then live AI text or static fallback */}
      <div className="mb-2">
        {rationaleLoading ? (
          <div className="bg-bg-3 h-8 animate-pulse rounded" aria-label="Loading rationale" />
        ) : (
          <p className="text-text-2 text-xs leading-relaxed">{rationaleText}</p>
        )}
        {isAiGenerated && !rationaleLoading && (
          <span className="text-text-3 mt-1 flex items-center gap-1 text-[0.625rem]">
            <Tag className="h-2.5 w-2.5" aria-hidden="true" />
            AI-generated
          </span>
        )}
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleQualifiers();
        }}
        className="text-text-2 hover:text-text-0 mb-1 flex items-center gap-1 text-xs"
      >
        Eligibility
        <ChevronDown className={cn('h-3 w-3 transition-transform', qualOpen && 'rotate-180')} />
      </button>
      {qualOpen && (
        <div className="mb-2">
          <div className="text-text-3 mb-1 font-mono text-[0.625rem]">
            {qi.product.requiredMatches} of {qi.product.rules.length} must match
          </div>
          <ul className="space-y-0.5">
            {qi.product.rules.map((rule, i) => (
              <li
                key={`${qi.id}-${String(i)}`}
                className="text-text-2 flex items-center justify-between text-xs"
              >
                <span>{rule.label}</span>
                <span
                  title={rule.required ? 'Required — must always be met' : 'Not required'}
                  style={{ color: rule.required ? 'var(--cyan)' : 'var(--text-3)' }}
                >
                  {rule.required ? '★' : '☆'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* v7 .qi-card__tags — each metric is a bordered mono pill (SCRUM-368). */}
      <div className="mb-2.5 flex flex-wrap gap-1">
        <span className="bg-bg-4 border-border-0 text-text-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[0.625rem]">
          <strong className="text-text-1 font-mono font-semibold">
            {qi.count.toLocaleString('en-US')}
          </strong>{' '}
          customers
        </span>
        <span className="bg-bg-4 border-border-0 text-text-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[0.625rem]">
          <strong className="text-text-1 font-mono font-semibold">{qi.avgMatchPct}%</strong> avg
          match
        </span>
        <span className="bg-bg-4 border-border-0 text-text-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[0.625rem]">
          <strong className="text-text-1 font-mono font-semibold">
            {formatCompactMoney(qi.avgBalance)}
          </strong>{' '}
          avg balance
        </span>
      </div>

      <div className="mt-auto flex gap-2">
        {isApplied ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
            className="bg-bg-4 text-text-1 border-border-1 hover:border-brand-primary hover:text-brand-primary inline-flex h-7 flex-1 items-center justify-center rounded-md border text-xs font-semibold transition-colors"
          >
            Remove insight
          </button>
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
            className="bg-brand-primary inline-flex h-7 flex-1 items-center justify-center rounded-md border border-transparent text-xs font-semibold text-white transition-colors hover:border-[var(--cyan-soft)] hover:bg-[var(--cyan-soft)]"
          >
            Apply Insight
          </button>
        )}
      </div>
    </div>
  );
}
